import { ACCELERATOR_MODELS, acceleratorEntry, acceleratorModelsOn } from "./accelerator-table.js";
import { assertValidTopic } from "./event-subjects.js";
import { COMPUTE_CLASSES, TIER_MACHINE_FAMILIES, isNeverSpotFamily, isTierMachineFamily } from "./compute-class.js";
import { compareScaledK8sQuantities, readFixtureInfrastructure } from "./fixture-resources.js";
import { safeRuleLabel } from "./fixture-placement-rules.js";
import { BLOB_BUCKET_RULE_IDS, BLOB_BUCKET_RULE_MESSAGES, checkBlobBucketRules, runsAtHome } from "./blob-buckets.js";
import { RESERVED_SCHEMAS, SCHEMA_NAME_PATTERN } from "./fixture-schema-derivation.js";
import { APP_POOL_BINDING_PAYLOAD_MAX_BYTES, APP_POOL_CONCURRENCY_MAX, APP_POOL_GPU_CLASSES, APP_POOL_MAX_NODES_MAX, APP_POOL_TIER_CLOUDS, COMPUTE_VECTOR_KEYS, appPoolTierKey, appPoolTierSchema, fixtureInfrastructureSchema, isAppPoolGpuTier, serializedByteLength } from "./app-pool.js";
import { WORK_SUBJECT_SEGMENT } from "./work-subjects.js";
import { APP_POOL_BINDING_SEGMENT, APP_POOL_RUN_NOTICE_SEGMENT, APP_POOL_SUBJECT_SEGMENT } from "./app-pool-subjects.js";
import { parseCronExpression } from "./cron-expression.js";
import { OWN_SCHEMA_ILLEGAL_RULE, OWN_SCHEMA_RESERVED_RULE, READ_SCHEMA_RESERVED_RULE, checkFixtureDatabaseSchema } from "./fixture-database-schema-rules.js";
//#region src/app-pool-rules.ts
/**
* The cross-bundle publish rules for `pools`, `bindings` and `autoscalers` (2026-09-04-app-pools,
* WS-1 §3, chain A hop 2). Bound at BOTH publish doors: `tools/zion-app` `validate-app` and
* release-engine's Step 4j-c, each by name; the CP's binding-completeness test (I-26) proves the
* second.
*
* Two rules read `kernelRights` and not `pools` (2026-10-09-app-mail, WS-1):
*
* - `kernel-right-mail-requires-core-api` needs the app manifest and every fixture.
* - `kernel-right-mail-external-requires-send` (R-011) needs the app manifest only.
*
* Both sit here because `checkAppPools` is the one app-bundle rule set that both publish doors
* already bind. The `zion-app` door reports each of the two mail rules once, through
* `checkAppBundleRules`; `checkAppKernelRightsRules` in `tools/zion-app` does not repeat them.
*
* For the same reason `checkAppPools` runs the five blob bucket rules of `blob-buckets.ts` last
* (2026-10-13-blob-buckets, WS-1, defaults row F7): their ids are the tail of `APP_POOL_RULE_IDS`
* and their messages the tail of `APP_POOL_RULE_MESSAGES`.
*
* Every issue is `severity: 'error'`. A warning-capable module is `deprecated-surface-rules.ts`,
* kept apart so this one can keep the all-error invariant the pool and work-target modules pin.
*
* Every access is guarded and every value stays `unknown` until this module answers for it. At the
* CLI door these rules run when schema validation has already failed
* (`research/publish-rule-binding.md` §3). So a pool's `class` can be a number, `pools` can be an
* array, and a pool name can carry a control character. A rule that needs a well-typed value does
* not fire, and the door reports the schema error beside these issues.
*
* Every app-authored string passes `safeRuleLabel` before it is interpolated, in the message and in
* the `path`. Both sinks are the ones `fixture-placement-rules.ts` documents for the M-1 defence:
* the author's terminal at the CLI door, and the HTTP 400 body plus the log line at the CP door. A
* pool name is a `path` segment here, because `pools` is keyed by name, so the untrusted value
* reaches `path` on every pool rule. On a schema-valid name (`WORK_NAME_PATTERN`, 40 characters of
* `[a-z0-9-]`) the sanitiser is the identity, so a legal pointer survives it.
*
* A third sink reads the CP door's 400 body: the MCP tool `publish_app_version` of site-admin-api
* returns its `path` and `detail` to an agent. The M-1 defence bounds the bytes and the length of a
* value. It sets no prompt data boundary. This module cannot set one, for two reasons:
*
* 1. A pool name reaches `path` on every pool rule. A bucket name reaches it on the two pool bucket
*    rules, at `/pools/<pool>/blobStorage/buckets/<bucket>`.
* 2. The register (DES-4) names the value the rule read, so that the author knows what to edit.
*
* The boundary belongs to the MCP layer. Today the `CpServiceToolError` arm of
* `AppAdminToolsProvider` in site-admin-api returns the structured body, `detail` and `path`
* included, with no untrusted-content mark. Its success arm marks text through `renderToolResult`.
* The two region rules are the one exception to DES-4. They name the field `region` and never its
* value, because the field name alone tells the author the fix.
*/
/**
* The rule ids `checkAppPools` may emit. Gate names: never renamed for prose.
*
* Three are `checkFixtureDatabaseSchema`'s identity-independent tiers, imported from their
* declarations rather than restated, so the pool door and the fixture door report one spelling per
* refusal (SPB-1). The last five are `BLOB_BUCKET_RULE_IDS`, spread from their one declaration in
* `blob-buckets.ts`, because `checkAppPools` runs `checkBlobBucketRules` (2026-10-13-blob-buckets).
*/
const APP_POOL_RULE_IDS = [
	"pool-image-fixture-unknown",
	"pool-image-fixture-unreferenced",
	"pool-infrastructure-invalid",
	"pool-infrastructure-exceeds-fixture",
	"pool-infrastructure-audit-unsupported",
	"pool-infrastructure-requires-core-api",
	"pool-compute-max-exceeds-node",
	"pool-compute-default-exceeds-max",
	"pool-compute-max-missing-key",
	"pool-region-requires-out-of-mesh",
	"pool-blob-bucket-exceeds-fixtures",
	"pool-blob-bucket-requires-org-scope",
	"pool-blob-bucket-requires-home-per-attempt",
	"pool-concurrency-unreachable",
	"pool-container-runtime-requires-open-egress",
	"pool-gpu-requires-default-sandbox",
	"pool-gpu-count-required",
	"pool-gpu-count-not-allowed",
	"pool-class-or-tiers-required",
	"pool-tiers-refuses-field",
	"pool-max-nodes-required",
	"pool-tier-family-unknown",
	"pool-tier-accelerator-unavailable",
	"pool-tier-gpu-count-unavailable",
	"pool-tier-spot-unavailable",
	"pool-tier-duplicate",
	"pool-tiers-gpu-mismatch",
	"pool-tiers-max-nodes-exceeded",
	"pool-gpu-tier-requires-default-sandbox",
	"pool-tier-concurrency-unreachable",
	"pool-preempt-policy-requires-spot-tier",
	"pool-max-preemptions-requires-auto",
	"pool-standing-requires-compute-default",
	"pool-standing-refuses-compute-max",
	"pool-autoscaler-unknown",
	"pool-knob-max-out-of-range",
	"binding-pool-unknown",
	"binding-schedule-invalid",
	"binding-event-topic-reserved",
	"binding-payload-too-large",
	"binding-duplicate",
	"kernel-right-mail-requires-core-api",
	"kernel-right-mail-external-requires-send",
	READ_SCHEMA_RESERVED_RULE,
	OWN_SCHEMA_RESERVED_RULE,
	OWN_SCHEMA_ILLEGAL_RULE,
	...BLOB_BUCKET_RULE_IDS
];
/**
* The three ids the pool door shares with the fixture door (R-036). `checkPoolDatabaseSchema`
* reports them at a pool's pointer, and the per-fixture pass reports the same three at a fixture's
* own `infrastructure.database` block.
*
* A caller that bounds a run to the ids only the app-bundle door may emit subtracts this set from
* `APP_POOL_RULE_IDS`. `tools/zion-app`'s plain `validate` run does that. Without the subtraction
* such a gate reports a leaked app-bundle id when the truth is a fixture-door rule firing on a
* fixture's own block.
*/
const APP_POOL_SHARED_RULE_IDS = [
	READ_SCHEMA_RESERVED_RULE,
	OWN_SCHEMA_RESERVED_RULE,
	OWN_SCHEMA_ILLEGAL_RULE
];
/**
* The messages, as data, one per rule id.
*
* The register is the one `fixture-work-target-rules.ts` uses (DES-4). Each message names the field
* and the value the rule read, says what breaks at run time, and ends with one plain-verb
* instruction. An author who reads only the message knows which key to edit and why.
*
* Every argument arrives sanitised. The caller passes app-authored values through `safeRuleLabel`,
* so the M-1 defence lives in one place and there is one place to audit.
* `binding-event-topic-reserved` interpolates `APP_POOL_SUBJECT_SEGMENT`,
* `APP_POOL_BINDING_SEGMENT` and `WORK_SUBJECT_SEGMENT` from their declarations (SEC-10, ARCH-18).
*/
const APP_POOL_RULE_MESSAGES = {
	"pool-image-fixture-unknown": ({ pool, fixture }) => `pool '${pool}' names fixture '${fixture}', which this bundle does not contain. The platform has no image to run, so no attempt ever starts. Name a fixture the bundle carries.`,
	"pool-image-fixture-unreferenced": ({ fixture }) => `fixture '${fixture}' declares 'poolImage: true' and no pool names it. The platform builds and publishes the image and nothing runs it. Point a pool at it or drop 'poolImage'.`,
	"pool-infrastructure-invalid": ({ pool, detail }) => `pool '${pool}' has an invalid 'infrastructure' block: ${detail}. The operator reads this block to decide which credentials and sidecars mount, so an invalid block mounts none of them. Correct the block against a fixture's 'infrastructure'.`,
	"pool-compute-max-exceeds-node": ({ pool, key, value, node }) => `pool '${pool}' asks for 'compute.max.${key}' of '${value}', above 'node.${key}' of '${node}'. A pod that requests more than one node offers never schedules. Lower 'compute.max.${key}' or raise 'node.${key}'.`,
	"pool-compute-default-exceeds-max": ({ pool, key, value, max }) => `pool '${pool}' has 'compute.default.${key}' of '${value}' above 'compute.max.${key}' of '${max}'. The default is what a run gets when it asks for nothing, so every such run is refused. Lower the default.`,
	"pool-compute-max-missing-key": ({ pool, key }) => `pool '${pool}' sets 'compute.default.${key}' and no 'compute.max.${key}'. A key with no ceiling is refused at submit, so every run on this pool fails admission. Give 'compute.max' a value for ${key}.`,
	"pool-region-requires-out-of-mesh": ({ pool }) => `pool '${pool}' declares 'region' on an in-mesh pool. An in-mesh member reaches the mesh, NATS and core-api of its data plane's home cluster, and a satellite cluster in another region carries none of them, so the pool could never run there. Remove 'region' or make the pool out-of-mesh.`,
	"pool-blob-bucket-exceeds-fixtures": ({ pool, bucket }) => `pool '${pool}' writes its attempt output to bucket '${bucket}' through 'blobStorage.buckets', and no fixture of this app that runs a workload binds '${bucket}' with 'readWrite' in 'infrastructure.blobStorage'. An out-of-mesh attempt runs with no app identity, so only a fixture workload of the app can read that output. A fixture with 'poolImage: true' and no services, workers, jobs or targets runs only as a pool image, so it does not count. Bind '${bucket}' with 'readWrite' in a fixture that runs a workload, or drop 'blobStorage' from the pool.`,
	"pool-blob-bucket-requires-org-scope": ({ pool, bucket }) => `pool '${pool}' writes its attempt output to bucket '${bucket}' through 'blobStorage.buckets', and app.json declares '${bucket}' with scope 'global'. The platform keeps one global bucket for every org, so each org could read the attempt output of every other org. Declare '${bucket}' with scope 'org', or name a bucket of scope 'org' in the pool.`,
	"pool-blob-bucket-requires-home-per-attempt": ({ pool, standing, regional }) => `pool '${pool}' declares 'blobStorage' with ${blobStorageHomeFields(standing, regional)}. The blob proxy writes the bucket only for a per-attempt pod in the home cluster: a standing member starts before any attempt, and a satellite cluster in a region runs no blob proxy. So the attempt output of this pool would reach no bucket. Drop 'blobStorage', or make the pool 'lifetime: per-attempt' with no 'region'.`,
	"pool-concurrency-unreachable": ({ pool, concurrency, key, value }) => `pool '${pool}' asks for 'concurrency' of ${concurrency} at 'compute.default.${key}' of '${value}', which needs more ${key} than 'maxNodes' times 'node.${key}' supplies. The pool can never reach its declared concurrency. Raise 'maxNodes' or lower 'concurrency'.`,
	"pool-container-runtime-requires-open-egress": ({ pool }) => `pool '${pool}' declares 'containerRuntime: true' with 'egress: closed'. A container runtime pulls images, and closed egress denies every pull, so the first attempt fails. Set 'egress: open' or drop 'containerRuntime'.`,
	"pool-gpu-requires-default-sandbox": ({ pool, computeClass, cpuClasses }) => `pool '${pool}' declares class '${computeClass}' with 'containerRuntime: true'. A GPU hands the sandbox the host GPU device and the NVIDIA driver, and the container-runtime arm runs with seccomp and AppArmor unconfined, so Zion refuses the two together. Drop 'containerRuntime', or use a CPU class (${cpuClasses}).`,
	"pool-gpu-count-required": ({ pool, max }) => `pool '${pool}' declares class 'gpu-training' and no 'gpuCount'. A training machine type sells a fixed GPU count, so the count selects the machine type and the 'node' vector does not, and with no count the pool never places. Declare 'gpuCount' from 1 to ${max}.`,
	"pool-gpu-count-not-allowed": ({ pool, computeClass }) => `pool '${pool}' declares 'gpuCount' on class '${computeClass}', where it means nothing. Only 'gpu-training' takes a GPU count, and for 'gpu-inference' the machine type sets the accelerator count, so the platform refuses the ask and the pool never places. Remove 'gpuCount', or declare class 'gpu-training' for a multi-GPU training pool.`,
	"pool-class-or-tiers-required": ({ pool }) => `pool '${pool}' declares neither 'class' nor 'tiers'. The platform derives the machine type from one of them, so with neither the pool never places. Declare 'class', or declare 'tiers' with the ordered machine tiers.`,
	"pool-tiers-refuses-field": ({ pool, field }) => `pool '${pool}' declares 'tiers' and '${field}'. A pool with tiers takes its machines, GPU counts and node caps from its tiers, so the platform never reads '${field}'. Remove '${field}', or remove 'tiers'.`,
	"pool-max-nodes-required": ({ pool }) => `pool '${pool}' declares no 'tiers' and no 'maxNodes'. A pool with a class has no other node cap, so its node group has no upper bound. Declare 'maxNodes'.`,
	"pool-tier-family-unknown": ({ pool, index, family, cloud, families }) => `pool '${pool}' tier ${index} names family '${family}' on cloud '${cloud}', which is not a CPU machine family Zion places there. The data plane finds no machine type for the tier, so the tier never opens. Name one of ${families}, or name a GPU with 'accelerator'.`,
	"pool-tier-accelerator-unavailable": ({ pool, index, accelerator, cloud, models }) => `pool '${pool}' tier ${index} names accelerator '${accelerator}' on cloud '${cloud}', and that cloud sells no shape of it. The tier can never place a node. Name a model that '${cloud}' sells (${models}), or move the tier to a cloud that sells '${accelerator}'.`,
	"pool-tier-gpu-count-unavailable": ({ pool, index, accelerator, cloud, gpuCount, counts }) => `pool '${pool}' tier ${index} asks for ${gpuCount} '${accelerator}' GPUs per node on cloud '${cloud}', and that cloud sells '${accelerator}' nodes with ${counts} GPUs only. No machine type matches, so the tier never places. Set 'gpuCount' to one of those counts.`,
	"pool-tier-spot-unavailable": ({ pool, index, subject, cloud }) => `pool '${pool}' tier ${index} asks for spot capacity on ${subject} on cloud '${cloud}', and that cloud never sells it as spot. The tier would wait in backoff for capacity that cannot come. Set 'capacity' to 'on-demand' on the tier.`,
	"pool-tier-duplicate": ({ pool, index, first, key }) => `pool '${pool}' tier ${index} repeats tier ${first}: both are '${key}'. Two equal tiers share one node group, so the second adds no machines and its cap is lost. Merge the two tiers into one.`,
	"pool-tiers-gpu-mismatch": ({ pool, index, found, firstIndex, first, cloud }) => `pool '${pool}' tier ${index} asks for ${found}, and tier ${firstIndex}, the first '${cloud}' tier, asks for ${first}. On a '${cloud}' data plane every attempt pod of the pool asks one GPU count, so it cannot fit the nodes of both tiers. Give every '${cloud}' tier the same accelerator count, or give no '${cloud}' tier an accelerator.`,
	"pool-tiers-max-nodes-exceeded": ({ pool, cloud, total, max }) => `pool '${pool}' declares '${cloud}' tiers whose caps add up to ${total} nodes, above the ceiling of ${max} nodes for one pool. On a '${cloud}' data plane the pool's node maximum is the sum of those caps, so the pool asks for more nodes than a pool may hold. Lower the '${cloud}' tier caps to ${max} nodes in total.`,
	"pool-gpu-tier-requires-default-sandbox": ({ pool, index, accelerator }) => `pool '${pool}' tier ${index} names accelerator '${accelerator}' with 'containerRuntime: true'. A GPU hands the sandbox the host GPU device and the NVIDIA driver, and the container-runtime arm runs with seccomp and AppArmor unconfined, so Zion refuses the two together. Drop 'containerRuntime', or use CPU tiers only.`,
	"pool-tier-concurrency-unreachable": (args) => args.key === "gpu" ? `pool '${args.pool}' asks for 'concurrency' of ${args.concurrency}, and its '${args.cloud}' GPU tiers hold ${args.capacity} nodes in total. Each attempt takes every GPU of its node, so one node runs one attempt, and on a '${args.cloud}' data plane the pool never reaches its concurrency. Raise the '${args.cloud}' tier caps or lower 'concurrency'.` : `pool '${args.pool}' asks for 'concurrency' of ${args.concurrency} at 'compute.default.${args.key}' of '${args.value}', and its '${args.cloud}' tiers hold ${args.capacity} nodes in total. That is less ${args.key} than the concurrency needs, so on a '${args.cloud}' data plane the pool never reaches it. Raise the '${args.cloud}' tier caps or lower 'concurrency'.`,
	"pool-preempt-policy-requires-spot-tier": ({ pool, field }) => `pool '${pool}' declares '${field}' and no tier with 'capacity: spot'. Only a spot node is reclaimed, so the platform never reads '${field}' on this pool. Remove '${field}', or add a spot tier.`,
	"pool-max-preemptions-requires-auto": ({ pool, policy }) => `pool '${pool}' declares 'maxPreemptions' with 'onPreempt' ${policy}. Only the 'auto' policy runs a preempted attempt again, so the bound is never read. Set 'onPreempt' to 'auto', or remove 'maxPreemptions'.`,
	"pool-infrastructure-exceeds-fixture": ({ pool, fixture, key }) => `pool '${pool}' declares 'infrastructure.${key}', which fixture '${fixture}' does not. A pool runs its fixture's image under that fixture's identity, so it reaches what the fixture reaches and no more. Declare it on the fixture, or drop it from the pool.`,
	"pool-infrastructure-audit-unsupported": ({ pool }) => `pool '${pool}' declares 'infrastructure.audit'. An app-pool producer holds no audit signing grant, so every attempt of this pool fails at reconcile and leaves nothing to read. Drop 'audit' from the pool; a fixture service or worker can still declare it.`,
	"pool-infrastructure-requires-core-api": ({ pool, member, mechanism }) => `pool '${pool}' declares 'infrastructure.${member}' without 'infrastructure.coreApiAccess'. ${mechanism}, and only 'coreApiAccess' supplies that URL, so this pool would never run the capability it declares. Add 'coreApiAccess' to the pool, or drop '${member}'.`,
	"pool-standing-requires-compute-default": ({ pool }) => `pool '${pool}' declares 'lifetime: standing' and no 'compute.default'. A standing member is sized once when it starts, so there is nothing to size it from. Add 'compute.default'.`,
	"pool-standing-refuses-compute-max": ({ pool }) => `pool '${pool}' declares 'lifetime: standing' with 'compute.max'. A standing member is sized once and a caller cannot narrow it, so the ceiling would never be read. Remove 'compute.max'.`,
	"pool-autoscaler-unknown": ({ pool, knob, algorithm }) => `pool '${pool}' sets '${knob}.algorithm' to '${algorithm}', which is not a key of 'autoscalers'. Nothing would evaluate the knob, so it would hold at its floor. Declare an autoscaler named '${algorithm}' or write a number.`,
	"pool-knob-max-out-of-range": ({ pool, knob, value, min, max }) => `pool '${pool}' sets '${knob}.max' to ${value}, outside the ${min} to ${max} range the field allows. A knob evaluated above its bound is clamped at reconcile, so the declared ceiling is a wrong number. Lower '${knob}.max'.`,
	"binding-pool-unknown": ({ index, pool }) => `binding ${index} names pool '${pool}', which this app does not declare. The trigger would fire into nothing. Name a pool from 'pools' or add it.`,
	"binding-schedule-invalid": ({ index, schedule, reason }) => `binding ${index} has 'on.schedule' of '${schedule}', which is not a five-field cron expression: ${reason}. The platform would never fire the binding. Correct the expression.`,
	"binding-event-topic-reserved": ({ index, topic, segment }) => `binding ${index} has 'on.event' of '${topic}', whose first segment '${segment}' is reserved by ` + (segment === "pool-run" ? "the platform. A durable on that subtree would read the platform's run-preempted notices, so each reclaim of a pool run would reach this binding as an event. Rename the topic." : "the platform. A durable on that subtree would read the platform's own run envelopes and fail verification on every one. Rename the topic."),
	"binding-payload-too-large": ({ index, bytes, max }) => `binding ${index} has a 'payload' of ${bytes} serialized bytes, above the ${max} byte limit. A payload above the limit cannot be projected to the data plane, so the pool would never receive the trigger. Shrink the payload.`,
	"binding-duplicate": ({ index, first }) => `binding ${index} repeats the pool and trigger of binding ${first}. Both would submit on every fire, so the pool receives each trigger twice. Delete one.`,
	"kernel-right-mail-requires-core-api": ({ right }) => `'kernelRights' names '${right}', and no fixture of this app declares 'infrastructure.coreApiAccess'. A mail goes out through the signer sidecar to core-api, and only 'coreApiAccess' gives that sidecar a core-api bearer, so every send of this app fails. Add 'coreApiAccess: true' to the fixture that sends mail, or drop '${right}'.`,
	"kernel-right-mail-external-requires-send": () => "'kernelRights' names 'mail.external' and not 'mail.send'. core-api checks 'mail.send' first on every send, so this app can never send a mail. Add 'mail.send' before 'mail.external', or drop 'mail.external'.",
	[READ_SCHEMA_RESERVED_RULE]: ({ pool, entry, reserved }) => `pool '${pool}' asks to read schema '${entry}' in 'infrastructure.database.readSchemas', and the platform owns ${reserved}. Those schemas hold per-organization signing keys and release state, so a pool granted SELECT on one reads platform secrets. Name a schema this app owns, or drop the entry.`,
	[OWN_SCHEMA_RESERVED_RULE]: ({ pool, value, reserved }) => `pool '${pool}' declares 'infrastructure.database.ownSchema' of '${value}', and the platform owns ${reserved}. A pool that owns a platform schema owns the platform's own state in that data plane. Remove the key: the platform derives the schema name from the app and the fixture.`,
	[OWN_SCHEMA_ILLEGAL_RULE]: ({ pool, value, pattern, max }) => `pool '${pool}' declares 'infrastructure.database.ownSchema' of '${value}', which Postgres refuses: a schema name matches ${pattern} and is at most ${max} characters. The operator cannot create the schema, so every attempt on the pool starts with no database. Remove the key and let the platform derive the name, or give a legal one.`,
	...BLOB_BUCKET_RULE_MESSAGES
};
/** The fields of `pool-blob-bucket-requires-home-per-attempt` that the pool declares, quoted, in
*  the order lifetime then region. The region field is named alone, with no value. */
function blobStorageHomeFields(standing, regional) {
	const fields = [];
	if (standing) fields.push(`'lifetime: standing'`);
	if (regional) fields.push(`'region'`);
	return fields.join(" and ");
}
/** A plain JSON object, or `null` for anything else (an array included). The manifests arrive as
*  `unknown` and nothing below assumes a type this has not answered for. */
function asRecord(v) {
	return typeof v === "object" && v !== null && !Array.isArray(v) ? v : null;
}
/** The value at `key`, as a non-empty string, or `null`. */
function asString(v) {
	return typeof v === "string" && v !== "" ? v : null;
}
/** One untrusted value as a JSON Pointer segment: sanitised by the M-1 defence first, then escaped
*  for the pointer grammar (RFC 6901 `~0`, `~1`). On a schema-valid pool name or fixture id both
*  steps are the identity. */
function pointerSegment(raw) {
	return safeRuleLabel(raw).replaceAll("~", "~0").replaceAll("/", "~1");
}
/** The three knobs and the range each one's platform bound allows. Read from `app-pool.ts`; the
*  numerals live there with their derivations (I-19) and are restated nowhere. */
const KNOB_BOUNDS = [
	{
		knob: "concurrency",
		min: 1,
		max: APP_POOL_CONCURRENCY_MAX
	},
	{
		knob: "warmFloor",
		min: 0,
		max: 50
	},
	{
		knob: "maxNodes",
		min: 1,
		max: APP_POOL_MAX_NODES_MAX
	}
];
/**
* The numeric value of a knob after any function arm, or `null` when the manifest does not give
* one. A finite number answers itself, a fraction included: the schema refuses the fraction, and a
* rule still compares it at its value (`compareScaledK8sQuantities` reads it exactly). A function
* knob answers `null`, because its value is decided at reconcile by the algorithm and no
* publish-time arithmetic may assume it.
*/
function knobNumber(v) {
	return typeof v === "number" && Number.isFinite(v) ? v : null;
}
/** The `{ algorithm, max }` arm of a knob, or `null`. `max` is read separately, because an author
*  can write an `algorithm` and a `max` of the wrong type. */
function functionKnob(v) {
	const r = asRecord(v);
	return r !== null && asString(r["algorithm"]) !== null ? r : null;
}
function issue(rule, path, message) {
	return {
		severity: "error",
		path,
		rule,
		message
	};
}
function checkPoolImage(ctx, out) {
	const image = asRecord(ctx.pool["image"]);
	const fixture = image === null ? null : asString(image["fixture"]);
	if (fixture === null || ctx.fixtureIds.has(fixture)) return;
	out.push(issue("pool-image-fixture-unknown", `${ctx.pointer}/image/fixture`, APP_POOL_RULE_MESSAGES["pool-image-fixture-unknown"]({
		pool: ctx.label,
		fixture: safeRuleLabel(fixture)
	})));
}
function checkPoolInfrastructure(ctx, out) {
	if (ctx.pool["workload"] !== "in-mesh" || ctx.pool["infrastructure"] === void 0) return;
	const parsedBlock = checkPoolInfrastructureShape(ctx, out);
	checkPoolDatabaseSchema(ctx, out);
	if (parsedBlock === null) return;
	checkPoolInfrastructureSubset(ctx, parsedBlock, out);
	checkPoolInfrastructureAudit(ctx, parsedBlock, out);
	checkPoolInfrastructureCoreApiCoupling(ctx, parsedBlock, out);
}
/** The structural arm: the block as a whole against the zod twin of `$defs/fixtureInfrastructure`.
*  It answers key sets and value types and nothing about a value's content. */
/**
* The block's shape, and the parsed value for whoever comes next.
*
* Returns the parsed block on success and `null` when it refused, having pushed
* `pool-infrastructure-invalid`. The return is what lets the value rules downstream take a parsed
* type instead of re-guarding an already-parsed object.
*/
function checkPoolInfrastructureShape(ctx, out) {
	const parsed = fixtureInfrastructureSchema.safeParse(ctx.pool["infrastructure"]);
	if (parsed.success) return parsed.data;
	const first = parsed.error.issues[0];
	const where = first.path.join(".");
	const detail = safeRuleLabel(where === "" ? first.message : `${where}: ${first.message}`);
	out.push(issue("pool-infrastructure-invalid", `${ctx.pointer}/infrastructure`, APP_POOL_RULE_MESSAGES["pool-infrastructure-invalid"]({
		pool: ctx.label,
		detail
	})));
	return null;
}
/** The reserved schemas, as the message renders them. One expression, from the one declaration. */
const RESERVED_SCHEMA_LIST = [...RESERVED_SCHEMAS].sort().join(", ");
/**
* The value-level arm on a pool's `infrastructure.database` block (SPB-1).
*
* `checkFixtureDatabaseSchema` decides and this function renders. The predicate for a reserved or
* an illegal declared value has one authored home (C-69, C-70). A second copy here would let the
* pool door and the fixture door disagree about which values are legal, the day that home widens.
* Before this arm existed the structural twin was the only gate of any kind on these values, and
* it accepts `ownSchema: 'auth'` and a 200-character name.
*
* Only the three identity-independent tiers are reported. The remaining three ids need the app
* slug and the fixture id, which a pool does not carry: `schema-not-derivable`,
* `schema-derivation-unverified` and `schema-derivation-blocked`. A pool names a fixture through
* `image.fixture`, and that fixture's own block is checked at the fixture door under its own
* identity. Two of the three are warnings and this module holds an all-error invariant, so passing
* them through would break the register as well as report an unanswerable question.
*
* The path comes from the issue rather than from a literal. So the fixture module owns the pointer
* spelling below `/infrastructure` and this door prefixes it. `FixtureRuleIssue.path` is
* documented as relative, for each door to prefix.
*/
function checkPoolDatabaseSchema(ctx, out) {
	const infra = asRecord(ctx.pool["infrastructure"]);
	const database = infra === null ? null : asRecord(infra["database"]);
	if (database === null) return;
	const readSchemas = Array.isArray(database["readSchemas"]) ? database["readSchemas"] : [];
	for (const found of checkFixtureDatabaseSchema({ infrastructure: { database } }, void 0, void 0)) {
		const path = `${ctx.pointer}${found.path}`;
		if (found.rule === "read-schema-reserved") {
			const index = Number(found.path.slice(found.path.lastIndexOf("/") + 1));
			out.push(issue(READ_SCHEMA_RESERVED_RULE, path, APP_POOL_RULE_MESSAGES[READ_SCHEMA_RESERVED_RULE]({
				pool: ctx.label,
				entry: safeRuleLabel(readSchemas[index]),
				reserved: RESERVED_SCHEMA_LIST
			})));
		} else if (found.rule === "own-schema-reserved") out.push(issue(OWN_SCHEMA_RESERVED_RULE, path, APP_POOL_RULE_MESSAGES[OWN_SCHEMA_RESERVED_RULE]({
			pool: ctx.label,
			value: safeRuleLabel(database["ownSchema"]),
			reserved: RESERVED_SCHEMA_LIST
		})));
		else if (found.rule === "own-schema-illegal") out.push(issue(OWN_SCHEMA_ILLEGAL_RULE, path, APP_POOL_RULE_MESSAGES[OWN_SCHEMA_ILLEGAL_RULE]({
			pool: ctx.label,
			value: safeRuleLabel(database["ownSchema"]),
			pattern: String(SCHEMA_NAME_PATTERN),
			max: 63
		})));
	}
}
function checkPoolCompute(ctx, out) {
	const compute = asRecord(ctx.pool["compute"]);
	const node = asRecord(ctx.pool["node"]);
	const max = compute === null ? null : asRecord(compute["max"]);
	const dflt = compute === null ? null : asRecord(compute["default"]);
	if (max !== null && node !== null) for (const key of COMPUTE_VECTOR_KEYS) {
		const order = compareScaledK8sQuantities(max[key], 1, node[key], 1);
		if (order === null || order <= 0) continue;
		out.push(issue("pool-compute-max-exceeds-node", `${ctx.pointer}/compute/max/${key}`, APP_POOL_RULE_MESSAGES["pool-compute-max-exceeds-node"]({
			pool: ctx.label,
			key,
			value: safeRuleLabel(max[key]),
			node: safeRuleLabel(node[key])
		})));
	}
	if (dflt !== null && max !== null) {
		for (const key of COMPUTE_VECTOR_KEYS) {
			const order = compareScaledK8sQuantities(dflt[key], 1, max[key], 1);
			if (order === null || order <= 0) continue;
			out.push(issue("pool-compute-default-exceeds-max", `${ctx.pointer}/compute/default/${key}`, APP_POOL_RULE_MESSAGES["pool-compute-default-exceeds-max"]({
				pool: ctx.label,
				key,
				value: safeRuleLabel(dflt[key]),
				max: safeRuleLabel(max[key])
			})));
		}
		for (const key of COMPUTE_VECTOR_KEYS) {
			if (dflt[key] === void 0 || max[key] !== void 0) continue;
			out.push(issue("pool-compute-max-missing-key", `${ctx.pointer}/compute/max`, APP_POOL_RULE_MESSAGES["pool-compute-max-missing-key"]({
				pool: ctx.label,
				key
			})));
		}
	}
}
/**
* A pool's `region` is legal only on an out-of-mesh pool (2026-09-29-gpu-and-regional-app-pools
* D-6).
*
* `pool-region-requires-out-of-mesh`: an in-mesh member needs the mesh, NATS and core-api, and a
* satellite cluster has none of them. The rule fires at `/pools/<name>/region` on either lifetime.
* 2026-10-02-regional-pools-release-2 deleted the second rule, `pool-region-requires-per-attempt`:
* the satellite now reconciles standing members from the per-region desired-state slice (that
* feature's §2.1, D-7), so a standing out-of-mesh pool with a region publishes clean (DF-8).
*
* The rule reads `region` through `asString`, so a region that is not a non-empty string raises
* nothing: the schema reports the type error, and a rule that cannot label the value stays quiet
* (the module header's convention). The message names the field and never the region value, so no
* region text reaches the caller of a publish door. A GPU class raises nothing here: D-20 admits it
* out of mesh, and `pool-gpu-requires-default-sandbox` refuses it on the container-runtime arm only
* (R-032).
*/
function checkPoolRegion(ctx, out) {
	if (asString(ctx.pool["region"]) === null || ctx.pool["workload"] !== "in-mesh") return;
	out.push(issue("pool-region-requires-out-of-mesh", `${ctx.pointer}/region`, APP_POOL_RULE_MESSAGES["pool-region-requires-out-of-mesh"]({ pool: ctx.label })));
}
/**
* Two rules on the bucket that receives the attempt output of an out-of-mesh pool
* (2026-10-22-pool-attempt-blob-reads, WS-1).
*
* `pool-blob-bucket-exceeds-fixtures`: no fixture of the app that runs a workload binds the name
* with `readWrite`. Why: the attempt pod holds no app identity, and the blob proxy writes for it.
* The app reads the output through a fixture workload. A fixture whose only surface is
* `poolImage: true` runs only as the image of its pools, so it reads nothing. The pool's mode is
* always `readWrite` (D-3). A fixture binding of `read` is narrower than the pool's, and the rule
* refuses it as it refuses no binding (D-1: refuse over accept).
*
* `pool-blob-bucket-requires-org-scope`: `app.blobStorage.buckets[]` declares the name with
* `scope: 'global'`. Why: the platform keeps one global bucket for every org. The attempt output of
* one org would then sit where every other org of the app can read it (D-1).
*
* Input: the pool, {@link PoolContext.fixtureReadWriteBuckets} and
* {@link PoolContext.globalScopeBuckets}. Read only when `workload` is `out-of-mesh` and
* `blobStorage.buckets` is a plain object. Each own key counts, whatever its value: a value other
* than `readWrite` is the schema layer's refusal, and both rules still read the name.
*
* Output: per name in key order, first the scope issue when the name is global, then the fixture
* issue when no fixture binds it. Both sit at `/pools/<pool>/blobStorage/buckets/<name>`. The name
* passes the M-1 defence in the path and in the message. An undeclared name also raises
* `blob-bucket-binding-declared`; the rules answer separate questions. Pure; never throws.
*/
function checkPoolBlobStorage(ctx, out) {
	if (ctx.pool["workload"] !== "out-of-mesh") return;
	const buckets = asRecord(asRecord(ctx.pool["blobStorage"])?.["buckets"]);
	if (buckets === null) return;
	for (const name of Object.keys(buckets)) {
		const path = `${ctx.pointer}/blobStorage/buckets/${pointerSegment(name)}`;
		const args = {
			pool: ctx.label,
			bucket: safeRuleLabel(name)
		};
		if (ctx.globalScopeBuckets.has(name)) out.push(issue("pool-blob-bucket-requires-org-scope", path, APP_POOL_RULE_MESSAGES["pool-blob-bucket-requires-org-scope"](args)));
		if (!ctx.fixtureReadWriteBuckets.has(name)) out.push(issue("pool-blob-bucket-exceeds-fixtures", path, APP_POOL_RULE_MESSAGES["pool-blob-bucket-exceeds-fixtures"](args)));
	}
}
/**
* The pool bucket key is legal only on a per-attempt pool in the home cluster
* (2026-10-22-pool-attempt-blob-reads, WS-1b, R-012).
*
* `pool-blob-bucket-requires-home-per-attempt`: an out-of-mesh pool that declares `blobStorage`
* together with `lifetime: 'standing'` or a `region`. Why: the blob proxy writes the bucket for a
* per-attempt pod at home alone. A standing member starts before any attempt and gets no blob
* group, and the satellite executor runs no blob proxy. So the key would bind a bucket that nothing
* writes, and the app would read no attempt output from it. D-1: refuse at publish. The operator
* refusal `poolBucketRefusal` in `@zion/zion-operator` stays as the second guard (R-012).
*
* Input: the pool. Read only when `workload` is `out-of-mesh` and the pool carries an own
* `blobStorage` key whose value is not `undefined`. Any such value counts, a malformed one
* included: the key is what the pool cannot carry, and its shape is the schema layer's refusal.
* `lifetime` counts only as the literal `standing`. `region` counts only as a non-empty string
* (`asString`): a region of another type is the schema layer's refusal, and the rule cannot label it.
*
* Output: at most one issue per pool, at `/pools/<pool>/blobStorage`. Its message names each field
* that blocks the key, `lifetime: standing` first and `region` next. The message carries no region
* value. A publish door returns the message to its caller, an MCP agent included. A region is text
* that the author of the bundle chose. An in-mesh pool raises nothing here: the schema refuses the
* key there, and `pool-region-requires-out-of-mesh` refuses its region. The two rules of
* {@link checkPoolBlobStorage} still read the same key; the rules answer separate questions. Pure;
* never throws.
*/
function checkPoolBlobStorageHome(ctx, out) {
	if (ctx.pool["workload"] !== "out-of-mesh" || ownValue(ctx.pool, "blobStorage") === void 0) return;
	const standing = ctx.pool["lifetime"] === "standing";
	const regional = asString(ctx.pool["region"]) !== null;
	let args;
	if (standing) args = {
		pool: ctx.label,
		standing,
		regional
	};
	else if (regional) args = {
		pool: ctx.label,
		standing,
		regional
	};
	else return;
	out.push(issue("pool-blob-bucket-requires-home-per-attempt", `${ctx.pointer}/blobStorage`, APP_POOL_RULE_MESSAGES["pool-blob-bucket-requires-home-per-attempt"](args)));
}
/**
* `compareScaledK8sQuantities` for a factor of either sign: `-1`, `0` or `1` as `leftFactor` times
* `left` compares with `rightFactor` times `right`, exactly, or `null` when either quantity is
* outside the grammar or either factor is `NaN` or infinite.
*
* Why. The rules read knobs that the schema may refuse, and at the CLI door they run after a
* schema failure. A negative `maxNodes` or `concurrency` must compare at its value, as the float
* compare before the exact one did, so a negative node cap still reads as a shortfall.
* `compareScaledK8sQuantities` answers `null` for a negative factor, so this reads the magnitudes
* and applies the signs. A quantity is never negative, so the sign of a product is the sign of its
* factor, or 0. Pure.
*/
function compareSignedScaled(left, leftFactor, right, rightFactor) {
	const magnitude = compareScaledK8sQuantities(left, Math.abs(leftFactor), right, Math.abs(rightFactor));
	if (magnitude === null) return null;
	const leftNegative = leftFactor < 0;
	if (leftNegative === rightFactor < 0) {
		if (!leftNegative || magnitude === 0) return magnitude;
		return magnitude === 1 ? -1 : 1;
	}
	if (magnitude === 0 && compareScaledK8sQuantities(left, Math.abs(leftFactor), "0", 1) === 0) return 0;
	return leftNegative ? -1 : 1;
}
/**
* The first compute key at which `nodes` times `node.<key>` supplies less than `concurrency`
* times `compute.default.<key>`, in `COMPUTE_VECTOR_KEYS` order, with the `compute.default` value
* the author wrote for it, or `null`. The compare is exact (`compareSignedScaled`), a fractional
* or negative knob included. A key that either vector does not set, or sets outside the quantity
* grammar, compares nothing. A pool with no `compute.default` or no `node` object gives `null`.
*
* `node.<key>` is the minimum machine shape the pool declares (L-10), and the only capacity per
* node that publish knows: the resolved machine type is chosen on each data plane, from its own
* catalog. So the compare holds the pool's own declarations together, and the author who needs
* more per node raises `node`. Pure.
*/
function shortComputeKey(pool, nodes, concurrency) {
	const compute = asRecord(pool["compute"]);
	const dflt = compute === null ? null : asRecord(compute["default"]);
	const node = asRecord(pool["node"]);
	if (dflt === null || node === null) return null;
	for (const key of COMPUTE_VECTOR_KEYS) {
		const order = compareSignedScaled(node[key], nodes, dflt[key], concurrency);
		if (order !== null && order < 0) return {
			key,
			value: dflt[key]
		};
	}
	return null;
}
/**
* `pool-concurrency-unreachable`: a pool with `class` whose `maxNodes` nodes can never run
* `concurrency` attempts at once. It fires once per pool, at `/pools/<name>/concurrency`, on the
* first compute key in `COMPUTE_VECTOR_KEYS` order where `maxNodes` times `node.<key>` is less than
* `concurrency` times `compute.default.<key>` (`shortComputeKey`).
*
* The rule keeps the class-pool behaviour of the rule before tiers (R-016 of plan
* `2026-10-24-pool-machine-generations`), so a class pool that published before still publishes:
*
* - It stays quiet when `concurrency`, `maxNodes` or `warmFloor` is a function knob: the value is
*   decided at reconcile.
* - It has no GPU arm. A GPU class pool with no short compute key raises nothing here, whatever
*   its `maxNodes`.
*
* A fraction or a negative number, which the schema refuses, still compares at its value.
*
* A pool with `tiers` raises nothing here. Its pool-level `maxNodes` is the refusal
* `pool-tiers-refuses-field`, and its reachability is `pool-tier-concurrency-unreachable`.
*/
function checkPoolConcurrencyReachable(ctx, out) {
	if (ctx.pool["tiers"] !== void 0) return;
	const concurrency = knobNumber(ctx.pool["concurrency"]);
	const maxNodes = knobNumber(ctx.pool["maxNodes"]);
	if (concurrency === null || maxNodes === null) return;
	if (knobNumber(ctx.pool["warmFloor"]) === null) return;
	const short = shortComputeKey(ctx.pool, maxNodes, concurrency);
	if (short === null) return;
	out.push(issue("pool-concurrency-unreachable", `${ctx.pointer}/concurrency`, APP_POOL_RULE_MESSAGES["pool-concurrency-unreachable"]({
		pool: ctx.label,
		concurrency,
		key: short.key,
		value: safeRuleLabel(short.value)
	})));
}
/**
* Every member of `fixtureInfrastructureSchema` this rule compares, and the predicate each one's
* value type implies (C-7). A member absent from every list below is compared by nothing.
*
* ⛔ `redis` IS ABSENT ON PURPOSE. It is dead on the fixture side and the pool side, in the schema
* text and in the operator's render path, and a rule must not police a dead member.
*
* ⛔ `audit` IS ALSO ABSENT, AND FOR THE OPPOSITE REASON.
* `checkPoolInfrastructureAudit` refuses it outright, so listing it here too would report one key
* under two ids whenever the fixture also lacks it. One key, one rule.
*/
const SUBSET_BOOLEAN_MEMBERS = [
	"coreApiAccess",
	"artifactRegistryRead",
	"multiOrgAgentHost",
	"machineEnrollment",
	"orgCrypto",
	"llm",
	"eventBus",
	"consumesAllStreams"
];
const SUBSET_ARRAY_MEMBERS = ["coordinationKv", "consumesStreams"];
/**
* An own-property read that cannot reach the prototype chain.
*
* ⚠️ IT IS DEFENCE IN DEPTH TODAY, AND THE THING DEFENDING IS A REGEX SOMEWHERE ELSE.
* The only member with app-chosen keys is `platformSecrets`, whose key schema is
* `/^[A-Z][A-Z0-9_]*$/`. `__proto__`, `constructor` and `prototype` are all lower-case, so none of
* them parses today and no dangerous key reaches a lookup. That regex is the guard, it lives in
* `fixtureInfrastructureSchema` rather than here, and a widening of it would arrive with no signal
* at this site. So the read is written safely and the reason is written down.
*/
function ownValue(obj, key) {
	return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : void 0;
}
/** `true` when every element of `pool` appears in `fixture`. A non-array pool value never narrows. */
function arrayWithin(poolValue, fixtureValue) {
	if (!Array.isArray(poolValue)) return true;
	const allowed = Array.isArray(fixtureValue) ? fixtureValue : [];
	return poolValue.every((entry) => allowed.includes(entry));
}
/**
* The logical names a fixture's `blobStorage` block binds, each with its mode (2026-10-13-blob-buckets,
* WS-1). The block is a reference that this module did not parse, so every read is guarded:
*
* - a string `bucket` (the legacy form) binds that name with `readWrite`;
* - each own key of an object `buckets` (the named form) whose value is `read` or `readWrite` binds
*   that name with that mode; any other value binds nothing.
*
* A block with both forms keeps both; the schema layer refuses such a block. When both forms bind
* one name, the wider mode wins: a named `read` never narrows the legacy `readWrite` of the same
* name. Both publish doors run these rules on a block that failed its schema, so the rules must not
* add a false refusal beside the schema error. The result is a `Map`, so a name such as
* `constructor` never reads the prototype chain.
*/
function boundBlobModes(block) {
	const modes = /* @__PURE__ */ new Map();
	const legacy = ownValue(block, "bucket");
	if (typeof legacy === "string") modes.set(legacy, "readWrite");
	for (const [name, mode] of Object.entries(asRecord(ownValue(block, "buckets")) ?? {})) if (mode === "readWrite" || mode === "read" && !modes.has(name)) modes.set(name, mode);
	return modes;
}
/**
* The `infrastructure` members that materialise on the fixture's ServiceAccount rather than on the
* pod, so a per-pod comparison has nothing to check for them (C-23).
*
* `artifactRegistryRead` annotates the fixture's KSA with a registry-reader GSA, which
* `FixtureDeployer`'s `artifactRegistryReadGsaFor` derives from the fixture MANIFEST and operator
* config. An in-mesh pool pod runs under that same admitted KSA, so it holds the capability the
* moment the fixture does, and nothing is rendered per pod that could be missing.
*
* ⛔ IT IS ALSO ABSENT FROM `FixtureDesiredResources`, AND THAT IS THE SYMPTOM RATHER THAN THE
* REASON. A render-time comparison against the allocated block reads `undefined` for it and would
* refuse every attempt of any pool declaring it, which is this feature's own defect in a new
* message. The reason it is skipped is the mechanism above; the absent field is how the defect
* would have surfaced.
*/
const SERVICE_ACCOUNT_CAPABILITY_MEMBERS = ["artifactRegistryRead"];
/** The two members of a `platformSecrets` entry that name a resource. `description` is cosmetic. */
const PLATFORM_SECRET_RESOURCE_KEYS = ["byteLength", "encoding"];
/**
* The first member a pool declares and its fixture does not, or `null` when the pool is within it.
*
* The walk is the pool's declaration, never the fixture's: a fixture may hold more than its pool
* asks for, which is the narrowing the rule admits.
*
* ⚠️ IT REPORTS ONE MEMBER AND NOT EVERY MEMBER (C-16). The subset rule is a relationship between
* two blocks, so naming the first divergence is what an author acts on. The sibling
* `read-schema-reserved` does emit one issue per entry, because there each entry is independently
* illegal against a fixed list; here the same entry is legal or not depending on the other block.
*/
function firstExceededMember(poolBlock, fixtureBlock, ignore = []) {
	const skipped = (label) => ignore.includes(label);
	for (const member of SUBSET_BOOLEAN_MEMBERS) {
		if (skipped(member)) continue;
		if (poolBlock[member] === true && ownValue(fixtureBlock, member) !== true) return {
			segments: [member],
			label: member
		};
	}
	for (const member of SUBSET_ARRAY_MEMBERS) {
		if (skipped(member)) continue;
		if (!arrayWithin(poolBlock[member], ownValue(fixtureBlock, member))) return {
			segments: [member],
			label: member
		};
	}
	const poolDb = poolBlock.database;
	if (poolDb !== void 0 && !skipped("database")) {
		const fixtureDb = asRecord(ownValue(fixtureBlock, "database"));
		if (fixtureDb === null) return {
			segments: ["database"],
			label: "database"
		};
		if (!skipped("database.ownSchema") && poolDb.ownSchema !== void 0 && poolDb.ownSchema !== ownValue(fixtureDb, "ownSchema")) return {
			segments: ["database", "ownSchema"],
			label: "database.ownSchema"
		};
		if (!skipped("database.readSchemas") && !arrayWithin(poolDb.readSchemas, ownValue(fixtureDb, "readSchemas"))) return {
			segments: ["database", "readSchemas"],
			label: "database.readSchemas"
		};
		if (!skipped("database.search") && poolDb.search === true && ownValue(fixtureDb, "search") !== true) return {
			segments: ["database", "search"],
			label: "database.search"
		};
	}
	const poolBlob = poolBlock.blobStorage;
	if (poolBlob !== void 0 && !skipped("blobStorage")) {
		const fixtureBlob = asRecord(ownValue(fixtureBlock, "blobStorage"));
		if (fixtureBlob === null) return {
			segments: ["blobStorage"],
			label: "blobStorage"
		};
		const fixtureModes = boundBlobModes(fixtureBlob);
		if ("bucket" in poolBlob) {
			if (!skipped("blobStorage.bucket") && fixtureModes.get(poolBlob.bucket) !== "readWrite") return {
				segments: ["blobStorage", "bucket"],
				label: "blobStorage.bucket"
			};
			const fixtureMax = ownValue(fixtureBlob, "maxSizeGb");
			if (!skipped("blobStorage.maxSizeGb") && typeof poolBlob.maxSizeGb === "number" && typeof fixtureMax === "number" && poolBlob.maxSizeGb > fixtureMax) return {
				segments: ["blobStorage", "maxSizeGb"],
				label: "blobStorage.maxSizeGb"
			};
		} else for (const [name, mode] of Object.entries(poolBlob.buckets)) {
			const exceeded = {
				segments: [
					"blobStorage",
					"buckets",
					pointerSegment(name)
				],
				label: `blobStorage.buckets.${safeRuleLabel(name)}`
			};
			if (skipped(exceeded.label)) continue;
			const fixtureMode = fixtureModes.get(name);
			if (fixtureMode === void 0 || mode === "readWrite" && fixtureMode !== "readWrite") return exceeded;
		}
	}
	const poolSecrets = poolBlock.platformSecrets;
	if (poolSecrets !== void 0 && !skipped("platformSecrets")) {
		const fixtureSecrets = asRecord(ownValue(fixtureBlock, "platformSecrets")) ?? {};
		for (const [key, poolEntry] of Object.entries(poolSecrets)) {
			const exceeded = {
				segments: ["platformSecrets", pointerSegment(key)],
				label: `platformSecrets.${safeRuleLabel(key)}`
			};
			if (skipped(exceeded.label)) continue;
			const fixtureEntry = asRecord(ownValue(fixtureSecrets, key));
			if (fixtureEntry === null) return exceeded;
			for (const field of PLATFORM_SECRET_RESOURCE_KEYS) if (poolEntry[field] !== ownValue(fixtureEntry, field)) return exceeded;
		}
	}
	return null;
}
/**
* The first capability a pool declares that `reference` does not hold, or `null` when it is within
* it. One formula, two call sites, two different reference blocks (C-6).
*
* The publish path passes the backing fixture's MANIFEST block and ignores nothing. The operator's
* render passes the CP-ALLOCATED block and ignores
* {@link SERVICE_ACCOUNT_CAPABILITY_MEMBERS}, because those land on a ServiceAccount the pool
* already shares. A second comparison table in the operator is what INV-D3 forbids, so this is the
* export that keeps C-7's predicate in one place.
*
* It answers the member's label and not its pointer segments. A JSON pointer is the publish path's
* concern, and handing segments to a caller that emits no `FixtureRuleIssue` would invite a second
* spelling of the pointer.
*/
function firstExceededCapability(poolBlock, reference, ignore = []) {
	const exceeded = firstExceededMember(poolBlock, reference, ignore);
	return exceeded === null ? null : exceeded.label;
}
/**
* A pool may narrow its backing fixture's capabilities and never widen them (D-9).
*
* The fixture's Postgres role, NATS credential and Secrets are what a pool pod reaches, so a member
* the fixture does not hold names a credential that does not exist. That is the manifest which
* renders an `envFrom` at a missing Secret and fails the pod at start.
*
* Only the out-of-mesh arm is exempt, because it declares no `infrastructure` at all: the two arms
* are different types and `appPoolSchema` refuses the block there.
*/
function checkPoolInfrastructureSubset(ctx, poolBlock, out) {
	if (!ctx.backingFixture.resolved) return;
	const exceeded = firstExceededMember(poolBlock, ctx.backingFixture.block);
	if (exceeded === null) return;
	out.push(issue("pool-infrastructure-exceeds-fixture", `${ctx.pointer}/infrastructure/${exceeded.segments.join("/")}`, APP_POOL_RULE_MESSAGES["pool-infrastructure-exceeds-fixture"]({
		pool: ctx.label,
		fixture: safeRuleLabel(ctx.backingFixtureId),
		key: exceeded.label
	})));
}
/**
* The members an in-mesh pool may not declare without `coreApiAccess` (S-2, S2-3).
*
* ⛔ THE TABLE IS THE RULE, AND A SINGLE-MEMBER RULE IS WHAT LET THE SECOND MEMBER THROUGH. Round
* 1 closed `machineEnrollment` as one instance. The security lens then found the class still open
* by walking one branch over on the same builder. A row here is the whole edit for the next
* member, so a third cannot recur the same way.
*
* ⛔ MEMBERSHIP IS ONE QUESTION: DOES THIS CAPABILITY'S ENDPOINT DERIVE FROM `CORE_API_URL`?
* Both rows do, and `buildSignerSidecar` derives both and refuses to render without the URL. A
* member that needs no endpoint does not belong, whatever else it needs. `orgCrypto` is the
* measured example and it is deliberately ABSENT: its keys are `SIGNER_KMS_ENCRYPT_KEY` and
* `SIGNER_ENCRYPT_KEY`, both operator config, and `signer-server.mjs` gates `/v1/crypto/encrypt`
* on the encrypt key alone. On a DP where the composition patches `signer.encryptKmsKey` a pool
* declaring `orgCrypto` alone renders a working signer, so a refusal here would break a legal
* production declaration. Whether a DP configured a key is not a thing a publish door can see.
*
* The operator's render refuses both rows and `audit` from this same table (zion-infra#2847).
* `buildAppPoolSidecarSet` calls {@link unservablePoolCapabilities} before it builds any
* attachment, and throws its `AppPoolRenderRefusal` for the first entry. So the attempt arm
* reports `executor-unavailable` and the standing arm reports the pool's `unschedulableReason`,
* each with the publish rule's message. Before that refusal the builder's own outcomes differed.
* `machineEnrollment` alone threw a plain `Error` in `buildCapabilityGatedSidecars`, and the
* standing arm logged it and left the row at `ready`. `multiOrgAgentHost` alone threw nothing: the
* signer attaches only on `coreApiAccess || orgCrypto || audit`, so the pod rendered with no signer
* and the declared capability materialised nowhere. The publish rule is still the first door,
* because it is the one the author reads. The render is the door for a version published before
* this rule, which promote does not check again.
*
* ⚠️ THE SUBSET RULE CANNOT CATCH EITHER, AND THE REASON IS WORTH KEEPING. A pool whose fixture
* declares BOTH members is within its fixture on every per-member predicate, so C-7's comparison
* passes and is right to. This is a SAME-SIDE completeness rule about one declaration, where the
* subset rule is a cross-side comparison between two. C-7 ruled the implications out of scope as a
* class; these are in scope because each renders a pool that can never run the capability.
*/
const CORE_API_COUPLED_MEMBERS = [{
	member: "machineEnrollment",
	mechanism: "The enroll and revoke endpoints derive from the core-API URL"
}, {
	member: "multiOrgAgentHost",
	mechanism: "The capability and agent-bearer endpoints derive from the core-API URL"
}];
/**
* Every `infrastructure` member of one in-mesh pool block that no data plane can serve, or an empty
* array when the block declares none. One formula, two call sites (zion-infra#2847).
*
* The publish rules `pool-infrastructure-audit-unsupported` and
* `pool-infrastructure-requires-core-api` read it, and so does the operator's render,
* `buildAppPoolSidecarSet` in `@zion/zion-operator`. A version published before those two rules
* existed is not checked again on promote, so the render is the one door that sees it. A second
* copy of {@link CORE_API_COUPLED_MEMBERS} in the operator is what INV-D3 forbids.
*
* The answer depends on the one block alone. No operator config, fixture block or allocation
* changes it, so a member it names is refused on every data plane and on every cycle. That is
* why the operator may report it as a pool-wide reason.
*
* - `audit: true` answers one `pool-infrastructure-audit-unsupported` entry, whatever else the
*   block declares.
* - Each member of {@link CORE_API_COUPLED_MEMBERS} declared `true` while `coreApiAccess` is not
*   `true` answers one `pool-infrastructure-requires-core-api` entry.
*
* The order is the publish order: the audit entry first, then the coupled members in table order.
* The function is pure. It throws nothing and reads no state.
*
* @param poolBlock the pool's parsed `infrastructure` block.
* @param pool the label the messages render as the pool's name. The publish path passes
*   `safeRuleLabel` of the pool's key.
* @returns the entries, in the order above.
*/
function unservablePoolCapabilities(poolBlock, pool) {
	const entries = [];
	if (poolBlock.audit === true) entries.push({
		rule: "pool-infrastructure-audit-unsupported",
		member: "audit",
		message: APP_POOL_RULE_MESSAGES["pool-infrastructure-audit-unsupported"]({ pool })
	});
	if (poolBlock.coreApiAccess === true) return entries;
	for (const { member, mechanism } of CORE_API_COUPLED_MEMBERS) {
		if (poolBlock[member] !== true) continue;
		entries.push({
			rule: "pool-infrastructure-requires-core-api",
			member,
			message: APP_POOL_RULE_MESSAGES["pool-infrastructure-requires-core-api"]({
				pool,
				member,
				mechanism
			})
		});
	}
	return entries;
}
/**
* The publish issues of one rule, from the entries {@link unservablePoolCapabilities} answers.
* The pointer is the member's own key under the pool's `infrastructure`.
*/
function pushUnservableIssues(ctx, poolBlock, rule, out) {
	for (const entry of unservablePoolCapabilities(poolBlock, ctx.label)) {
		if (entry.rule !== rule) continue;
		out.push(issue(entry.rule, `${ctx.pointer}/infrastructure/${entry.member}`, entry.message));
	}
}
/**
* An in-mesh pool declaring any member of {@link CORE_API_COUPLED_MEMBERS} must declare
* `coreApiAccess` too (S-2, S2-3). One issue per declared member, so an author reading two
* refusals has two things to fix. The entries come from {@link unservablePoolCapabilities}.
*/
function checkPoolInfrastructureCoreApiCoupling(ctx, poolBlock, out) {
	pushUnservableIssues(ctx, poolBlock, "pool-infrastructure-requires-core-api", out);
}
/**
* An in-mesh pool may not declare `infrastructure.audit`.
*
* No data plane can run such a pool. `buildSignerSidecar`'s audit coupling throws at reconcile when
* an audit endpoint arrives with no brokered SA-token read grant, and that grant is a per-producer
* security decision that nobody has taken for this producer (C-052). This rule refuses the
* declaration at publish, the one place the author reads. The entry comes from
* {@link unservablePoolCapabilities}, which the operator's render also reads (zion-infra#2847). So
* the render refuses a version published before this rule with an `AppPoolRenderRefusal`, before
* the signer builder runs. The attempt arm then reports `executor-unavailable`, and the standing
* arm reports this rule's message as the pool's `unschedulableReason`.
*/
function checkPoolInfrastructureAudit(ctx, poolBlock, out) {
	pushUnservableIssues(ctx, poolBlock, "pool-infrastructure-audit-unsupported", out);
}
function checkPoolContainerRuntime(ctx, out) {
	if (ctx.pool["workload"] !== "out-of-mesh") return;
	if (ctx.pool["containerRuntime"] !== true || ctx.pool["egress"] !== "closed") return;
	out.push(issue("pool-container-runtime-requires-open-egress", `${ctx.pointer}/containerRuntime`, APP_POOL_RULE_MESSAGES["pool-container-runtime-requires-open-egress"]({ pool: ctx.label })));
}
/**
* The classes a GPU pool on the container-runtime arm can move to, as the message renders them:
* `'N', 'C' or 'M'`. Every member of `COMPUTE_CLASSES` outside `APP_POOL_GPU_CLASSES`, in the
* declaration's order, so a class added to either list reaches the message with no edit here.
*/
const CPU_COMPUTE_CLASS_LIST = (() => {
	const cpu = COMPUTE_CLASSES.filter((c) => !APP_POOL_GPU_CLASSES.includes(c)).map((c) => `'${c}'`);
	return `${cpu.slice(0, -1).join(", ")} or ${cpu[cpu.length - 1]}`;
})();
/**
* A GPU class runs on an out-of-mesh pool only on the default isolation arm
* (2026-09-29-gpu-and-regional-app-pools R-032, option 2; R-113).
*
* An out-of-mesh pool is the sealed sandbox that runs untrusted tenant code. A GPU class gives its
* pod the host GPU device and the host NVIDIA kernel driver's ioctl surface, on either arm. The
* `containerRuntime: true` arm also runs with seccomp and AppArmor `Unconfined`, so the two together
* widen what the job reaches outside its own namespaces past what Dave's ruling accepts. The default
* arm keeps `seccompProfile: RuntimeDefault` and drops every capability, and there the GPU is legal.
*
* Fires on an out-of-mesh pool whose `class` is one of `APP_POOL_GPU_CLASSES` and whose
* `containerRuntime` is `true`, at `/pools/<name>/containerRuntime`: the first fix the message
* names, and the pointer the sibling `pool-container-runtime-requires-open-egress` uses. An in-mesh
* pool is not read, because its arm refuses `containerRuntime` as an unknown key (I-28). A class
* that is not a non-empty string raises nothing, the module header's convention. The operator
* refuses the same pool at placement under the same id, so a bundle that predates this rule stays
* `placement: unschedulable` and never bills a GPU node (R-113).
*/
function checkPoolGpuSandbox(ctx, out) {
	if (ctx.pool["workload"] !== "out-of-mesh" || ctx.pool["containerRuntime"] !== true) return;
	const computeClass = asString(ctx.pool["class"]);
	if (computeClass === null || !APP_POOL_GPU_CLASSES.includes(computeClass)) return;
	out.push(issue("pool-gpu-requires-default-sandbox", `${ctx.pointer}/containerRuntime`, APP_POOL_RULE_MESSAGES["pool-gpu-requires-default-sandbox"]({
		pool: ctx.label,
		computeClass: safeRuleLabel(computeClass),
		cpuClasses: CPU_COMPUTE_CLASS_LIST
	})));
}
/**
* The class coupling of a pool's `gpuCount` (2026-10-02-regional-pools-release-2 D-9, DF-3).
*
* The predicate is `checkComputeClassDeclaration`'s CC-1a and CC-1b in `fixture-placement-rules.ts`,
* on a pool. `pool-gpu-count-required` fires when `class` is `gpu-training` and `gpuCount` is
* absent. `pool-gpu-count-not-allowed` fires when `gpuCount` is present and `class` is any other
* value, a missing or mistyped class included: refuse over accept (DF-3), the fixture rule's
* fail-closed reading. Both report at `/pools/<name>/gpuCount`, and at most one fires per pool.
*
* "Absent" is `undefined`, as in the fixture rule. A present value outside 1 to `MAX_GPU_COUNT`, or
* one that is not an integer, is the schema's refusal, so the required rule stays quiet on it. The
* class reaches the message through `safeRuleLabel`, so a mistyped class renders as `<number>` and
* a hostile string renders sanitised (the M-1 defence). Both arms are read, because `gpuCount` sits
* in `appPoolBaseShape`.
*/
function checkPoolGpuCount(ctx, out) {
	if (ctx.pool["tiers"] !== void 0) return;
	const computeClass = ctx.pool["class"];
	const gpuCount = ctx.pool["gpuCount"];
	if (computeClass === "gpu-training") {
		if (gpuCount !== void 0) return;
		out.push(issue("pool-gpu-count-required", `${ctx.pointer}/gpuCount`, APP_POOL_RULE_MESSAGES["pool-gpu-count-required"]({
			pool: ctx.label,
			max: 8
		})));
		return;
	}
	if (gpuCount === void 0) return;
	out.push(issue("pool-gpu-count-not-allowed", `${ctx.pointer}/gpuCount`, APP_POOL_RULE_MESSAGES["pool-gpu-count-not-allowed"]({
		pool: ctx.label,
		computeClass: safeRuleLabel(computeClass)
	})));
}
/** The pool-level fields a pool with `tiers` must not declare (D-16), in the order the rule
*  reports them. */
const TIERS_REFUSED_FIELDS = [
	"class",
	"gpuCount",
	"maxNodes"
];
/**
* The placement form of a pool (D-16): `class` or `tiers`, exactly one, and `maxNodes` with
* `class` only.
*
* - `pool-class-or-tiers-required`: neither key is present. Reported at the pool's pointer.
* - `pool-tiers-refuses-field`: `tiers` is present with `class`, `gpuCount` or `maxNodes`. One
*   issue per declared field, at that field's pointer.
* - `pool-max-nodes-required`: `tiers` is absent and `maxNodes` is absent. Reported at the pool's
*   pointer. It fires beside the first rule when both keys are absent, because the fix for the
*   first rule with `class` still needs `maxNodes`.
*
* Presence is `!== undefined`, whatever the value's type: a mistyped value is the schema's
* refusal, and the form rule still reads which keys the author wrote.
*/
function checkPoolPlacementForm(ctx, out) {
	const hasTiers = ctx.pool["tiers"] !== void 0;
	if (!hasTiers && ctx.pool["class"] === void 0) out.push(issue("pool-class-or-tiers-required", ctx.pointer, APP_POOL_RULE_MESSAGES["pool-class-or-tiers-required"]({ pool: ctx.label })));
	if (hasTiers) for (const field of TIERS_REFUSED_FIELDS) {
		if (ctx.pool[field] === void 0) continue;
		out.push(issue("pool-tiers-refuses-field", `${ctx.pointer}/${field}`, APP_POOL_RULE_MESSAGES["pool-tiers-refuses-field"]({
			pool: ctx.label,
			field
		})));
	}
	else if (ctx.pool["maxNodes"] === void 0) out.push(issue("pool-max-nodes-required", ctx.pointer, APP_POOL_RULE_MESSAGES["pool-max-nodes-required"]({ pool: ctx.label })));
}
/** The pool's `tiers` as an array of raw entries, or `[]` when it is absent or not an array. */
function rawTiers(pool) {
	return Array.isArray(pool["tiers"]) ? pool["tiers"] : [];
}
/**
* A list rendered for a message: `'a'`, or `'a', 'b' or 'c'`. Numbers render bare: `1 or 8`.
*
* Every caller passes a non-empty list, so the helper has no empty-list arm: the tier families of
* either cloud (`TIER_MACHINE_FAMILIES`, pinned by TF-4), the models of `gcp` or `aws`
* (`acceleratorModelsOn`, pinned by AE-3), and the `gpuCounts` of a table entry (never empty,
* pinned by AT-4). The `aws` model list was the last empty input, and WS-4a filled it.
*/
function quotedList(items) {
	const quoted = items.map((i) => typeof i === "number" ? String(i) : `'${i}'`);
	return quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(", ")} or ${quoted[quoted.length - 1]}`;
}
/**
* The per-tier rules, each over one raw tier entry (D-10, D-15, D-18, D-28, D-44). A tier that is
* not an object, or whose `cloud` is not `gcp` or `aws`, raises nothing here: the schema reports
* it.
*
* - `pool-tier-family-unknown`: a string `family` outside `TIER_MACHINE_FAMILIES[cloud]`, at
*   `/tiers/<i>/family`. A GPU family (`g2`, `a3`) is outside the list, so the message names
*   `accelerator`.
* - `pool-tier-accelerator-unavailable`: an `accelerator` from `ACCELERATOR_MODELS` with no table
*   entry for the tier's cloud, at `/tiers/<i>/accelerator`. A string outside the model list is
*   the schema's refusal and raises nothing here.
* - `pool-tier-gpu-count-unavailable`: an integer `gpuCount` the cloud does not sell for the
*   model, at `/tiers/<i>/gpuCount`. It needs an entry, so it never fires beside the previous rule.
* - `pool-tier-spot-unavailable`: `capacity: 'spot'` on a family the cloud never sells as spot, at
*   `/tiers/<i>/capacity`. A CPU tier reads its `family`; a GPU tier reads the families of its
*   table entry and fires when every one of them is never spot (`gb200` on `gcp` is `a4x`).
*/
function checkPoolTierEntries(ctx, out) {
	for (const [index, raw] of rawTiers(ctx.pool).entries()) {
		const tier = asRecord(raw);
		if (tier === null) continue;
		const cloud = tier["cloud"];
		if (cloud !== "gcp" && cloud !== "aws") continue;
		const at = `${ctx.pointer}/tiers/${index}`;
		const family = asString(tier["family"]);
		if (family !== null && !isTierMachineFamily(cloud, family)) out.push(issue("pool-tier-family-unknown", `${at}/family`, APP_POOL_RULE_MESSAGES["pool-tier-family-unknown"]({
			pool: ctx.label,
			index,
			family: safeRuleLabel(family),
			cloud,
			families: quotedList(TIER_MACHINE_FAMILIES[cloud])
		})));
		const accelerator = tier["accelerator"];
		const isModel = ACCELERATOR_MODELS.includes(accelerator);
		const entry = acceleratorEntry(accelerator, cloud);
		if (isModel && entry === null) out.push(issue("pool-tier-accelerator-unavailable", `${at}/accelerator`, APP_POOL_RULE_MESSAGES["pool-tier-accelerator-unavailable"]({
			pool: ctx.label,
			index,
			accelerator: String(accelerator),
			cloud,
			models: quotedList(acceleratorModelsOn(cloud))
		})));
		const gpuCount = tier["gpuCount"];
		if (entry !== null && Number.isInteger(gpuCount) && !entry.gpuCounts.includes(gpuCount)) out.push(issue("pool-tier-gpu-count-unavailable", `${at}/gpuCount`, APP_POOL_RULE_MESSAGES["pool-tier-gpu-count-unavailable"]({
			pool: ctx.label,
			index,
			accelerator: String(accelerator),
			cloud,
			gpuCount,
			counts: quotedList(entry.gpuCounts)
		})));
		if (tier["capacity"] !== "spot") continue;
		if (!(family !== null ? isNeverSpotFamily(cloud, family) : entry !== null && entry.families.every((f) => isNeverSpotFamily(cloud, f)))) continue;
		out.push(issue("pool-tier-spot-unavailable", `${at}/capacity`, APP_POOL_RULE_MESSAGES["pool-tier-spot-unavailable"]({
			pool: ctx.label,
			index,
			subject: family !== null ? `family '${safeRuleLabel(family)}'` : `accelerator '${String(accelerator)}'`,
			cloud
		})));
	}
}
/** The tiers that parse, each with its index in the declared list. A tier that does not parse is
*  the schema's refusal, and the rules over parsed tiers skip it. */
function parsedTiers(pool) {
	const out = [];
	for (const [index, raw] of rawTiers(pool).entries()) {
		const parsed = appPoolTierSchema.safeParse(raw);
		if (parsed.success) out.push({
			index,
			tier: parsed.data
		});
	}
	return out;
}
/** The GPU ask of a parsed tier, as a message renders it. */
function gpuAsk(tier) {
	return isAppPoolGpuTier(tier) ? `${tier.gpuCount} GPUs per node` : "no GPU";
}
/**
* The rules over the whole tier list, read from the tiers that parse (D-26):
*
* - `pool-tier-duplicate`: a tier whose `appPoolTierKey` equals an earlier tier's, at
*   `/tiers/<i>`, naming the first index. Two equal tiers hash to one node group id.
* - `pool-tiers-gpu-mismatch`: a tier whose GPU count (none for a CPU tier) differs from the GPU
*   count of the first parsed tier of the same cloud, at `/tiers/<i>`. A data plane keeps only the
*   tiers of its own cloud (D-15), and its attempt pods ask one GPU count, so the count must agree
*   inside one cloud and may differ between two clouds. The issues come in `APP_POOL_TIER_CLOUDS`
*   order, and in tier order inside one cloud.
*/
function checkPoolTierList(ctx, out) {
	const tiers = parsedTiers(ctx.pool);
	const seen = /* @__PURE__ */ new Map();
	for (const { index, tier } of tiers) {
		const key = appPoolTierKey(tier);
		const first = seen.get(key);
		if (first === void 0) {
			seen.set(key, index);
			continue;
		}
		out.push(issue("pool-tier-duplicate", `${ctx.pointer}/tiers/${index}`, APP_POOL_RULE_MESSAGES["pool-tier-duplicate"]({
			pool: ctx.label,
			index,
			first,
			key
		})));
	}
	for (const cloud of APP_POOL_TIER_CLOUDS) {
		const onCloud = tiers.filter(({ tier }) => tier.cloud === cloud);
		const head = onCloud[0];
		if (head === void 0) continue;
		for (const { index, tier } of onCloud.slice(1)) {
			if (gpuAsk(tier) === gpuAsk(head.tier)) continue;
			out.push(issue("pool-tiers-gpu-mismatch", `${ctx.pointer}/tiers/${index}`, APP_POOL_RULE_MESSAGES["pool-tiers-gpu-mismatch"]({
				pool: ctx.label,
				index,
				found: gpuAsk(tier),
				firstIndex: head.index,
				first: gpuAsk(head.tier),
				cloud
			})));
		}
	}
}
/**
* `pool-tiers-max-nodes-exceeded`: on one data plane a pool runs on the tiers of that data
* plane's cloud (D-15), and its node maximum there is the sum of their caps (D-25). That sum may
* not pass `APP_POOL_MAX_NODES_MAX`, the ceiling of one pool's `maxNodes` (DEF-1). For each cloud in
* `APP_POOL_TIER_CLOUDS` order whose parsed tiers sum past the ceiling, one issue at
* `/pools/<name>/tiers`. The sum counts each distinct tier key once (`tierNodeTotal`): a repeat
* shares the node group of its first tier. The tiers of two clouds never add up, because no data
* plane keeps both. JSON Schema cannot sum an array, so this rule is the only check of the total.
*/
function checkPoolTierNodeTotal(ctx, out) {
	const tiers = parsedTiers(ctx.pool);
	for (const cloud of APP_POOL_TIER_CLOUDS) {
		const total = tierNodeTotal(tiers.filter(({ tier }) => tier.cloud === cloud));
		if (total <= APP_POOL_MAX_NODES_MAX) continue;
		out.push(issue("pool-tiers-max-nodes-exceeded", `${ctx.pointer}/tiers`, APP_POOL_RULE_MESSAGES["pool-tiers-max-nodes-exceeded"]({
			pool: ctx.label,
			cloud,
			total,
			max: APP_POOL_MAX_NODES_MAX
		})));
	}
}
/**
* A GPU tier on an out-of-mesh pool runs only on the default isolation arm, the tier twin of
* `pool-gpu-requires-default-sandbox` (R-032). Fires once per pool, on an out-of-mesh pool with
* `containerRuntime: true` and at least one tier whose `accelerator` is a non-empty string, at
* `/pools/<name>/containerRuntime`. The message names the first such tier.
*/
function checkPoolGpuTierSandbox(ctx, out) {
	if (ctx.pool["workload"] !== "out-of-mesh" || ctx.pool["containerRuntime"] !== true) return;
	const tiers = rawTiers(ctx.pool);
	const index = tiers.findIndex((t) => asString(asRecord(t)?.["accelerator"]) !== null);
	if (index === -1) return;
	out.push(issue("pool-gpu-tier-requires-default-sandbox", `${ctx.pointer}/containerRuntime`, APP_POOL_RULE_MESSAGES["pool-gpu-tier-requires-default-sandbox"]({
		pool: ctx.label,
		index,
		accelerator: safeRuleLabel(asRecord(tiers[index])["accelerator"])
	})));
}
/**
* The node total of parsed tiers: the sum of the caps of the first tier of each `appPoolTierKey`.
* A repeat of a key shares the node group of its first tier and adds no machines
* (`pool-tier-duplicate`), so its cap adds nothing to the total.
*/
function tierNodeTotal(tiers) {
	const seen = /* @__PURE__ */ new Set();
	let total = 0;
	for (const { tier } of tiers) {
		const key = appPoolTierKey(tier);
		if (seen.has(key)) continue;
		seen.add(key);
		total += tier.maxNodes;
	}
	return total;
}
/**
* A parsed tier that can place a node on its cloud: a CPU tier whose `family` is in
* `TIER_MACHINE_FAMILIES`, or a GPU tier whose model has an `ACCELERATOR_TABLE` entry for the cloud
* that sells its `gpuCount`. A tier that fails these is refused by `pool-tier-family-unknown`,
* `pool-tier-accelerator-unavailable` or `pool-tier-gpu-count-unavailable`, and holds no node.
* Pure.
*/
function placeableTier(tier) {
	if (!isAppPoolGpuTier(tier)) return isTierMachineFamily(tier.cloud, tier.family);
	const entry = acceleratorEntry(tier.accelerator, tier.cloud);
	return entry !== null && entry.gpuCounts.includes(tier.gpuCount);
}
/**
* The tier twin of `pool-concurrency-unreachable` (D-25). On one data plane a pool runs on the
* tiers of that data plane's cloud, so its node maximum there is the sum of the caps of those tiers
* that can place a node (`placeableTier`), each distinct tier key counted once (`tierNodeTotal`).
* For each cloud in `APP_POOL_TIER_CLOUDS` order with such tiers, two arms compare, in this order:
*
* 1. A compute key: the sum times `node.<key>` is less than `concurrency` times
*    `compute.default.<key>` (`shortComputeKey`), in exact arithmetic.
* 2. GPU tiers: the first placeable tier of the cloud is a GPU tier and the sum is less than
*    `concurrency`. Each attempt pod takes every GPU of its node, so one node runs one attempt.
*    This arm needs no `compute.default`.
*
* It fires once per pool, for the first cloud and arm that fall short, at
* `/pools/<name>/concurrency`. It reads the integer arm of `concurrency` and stays quiet on a
* function knob. A fraction, which the schema refuses, still compares at its value. `warmFloor`
* sets no maximum, so the rule reads no `warmFloor`. `APP_POOL_TIER_CLOUDS` is frozen, so no
* consumer can remove a cloud from this loop.
*/
function checkPoolTierConcurrencyReachable(ctx, out) {
	const concurrency = knobNumber(ctx.pool["concurrency"]);
	if (concurrency === null) return;
	const tiers = parsedTiers(ctx.pool).filter(({ tier }) => placeableTier(tier));
	for (const cloud of APP_POOL_TIER_CLOUDS) {
		const onCloud = tiers.filter(({ tier }) => tier.cloud === cloud);
		const head = onCloud[0];
		if (head === void 0) continue;
		const capacity = tierNodeTotal(onCloud);
		const short = shortComputeKey(ctx.pool, capacity, concurrency);
		if (short !== null) {
			out.push(issue("pool-tier-concurrency-unreachable", `${ctx.pointer}/concurrency`, APP_POOL_RULE_MESSAGES["pool-tier-concurrency-unreachable"]({
				pool: ctx.label,
				concurrency,
				key: short.key,
				value: safeRuleLabel(short.value),
				cloud,
				capacity
			})));
			return;
		}
		if (!isAppPoolGpuTier(head.tier) || capacity >= concurrency) continue;
		out.push(issue("pool-tier-concurrency-unreachable", `${ctx.pointer}/concurrency`, APP_POOL_RULE_MESSAGES["pool-tier-concurrency-unreachable"]({
			pool: ctx.label,
			concurrency,
			key: "gpu",
			cloud,
			capacity
		})));
		return;
	}
}
/**
* The re-run policy fields (D-12, D-31):
*
* - `pool-preempt-policy-requires-spot-tier`: `onPreempt` or `maxPreemptions` is present and no
*   tier has `capacity: 'spot'` (a pool with `class` has none). Reported once, at `onPreempt`
*   when it is present, else at `maxPreemptions`.
* - `pool-max-preemptions-requires-auto`: `maxPreemptions` is present and `onPreempt` is not
*   `'auto'`, at `/pools/<name>/maxPreemptions`. The message names the declared policy, or says
*   that it is absent.
*/
function checkPoolPreemptPolicy(ctx, out) {
	const onPreempt = ctx.pool["onPreempt"];
	const maxPreemptions = ctx.pool["maxPreemptions"];
	if (onPreempt === void 0 && maxPreemptions === void 0) return;
	if (!rawTiers(ctx.pool).some((t) => asRecord(t)?.["capacity"] === "spot")) {
		const field = onPreempt !== void 0 ? "onPreempt" : "maxPreemptions";
		out.push(issue("pool-preempt-policy-requires-spot-tier", `${ctx.pointer}/${field}`, APP_POOL_RULE_MESSAGES["pool-preempt-policy-requires-spot-tier"]({
			pool: ctx.label,
			field
		})));
	}
	if (maxPreemptions === void 0 || onPreempt === "auto") return;
	out.push(issue("pool-max-preemptions-requires-auto", `${ctx.pointer}/maxPreemptions`, APP_POOL_RULE_MESSAGES["pool-max-preemptions-requires-auto"]({
		pool: ctx.label,
		policy: onPreempt === void 0 ? "absent" : `'${safeRuleLabel(onPreempt)}'`
	})));
}
function checkPoolStanding(ctx, out) {
	if (ctx.pool["lifetime"] !== "standing") return;
	const compute = asRecord(ctx.pool["compute"]);
	if (compute === null || compute["default"] === void 0) out.push(issue("pool-standing-requires-compute-default", `${ctx.pointer}/lifetime`, APP_POOL_RULE_MESSAGES["pool-standing-requires-compute-default"]({ pool: ctx.label })));
	if (compute !== null && compute["max"] !== void 0) out.push(issue("pool-standing-refuses-compute-max", `${ctx.pointer}/compute/max`, APP_POOL_RULE_MESSAGES["pool-standing-refuses-compute-max"]({ pool: ctx.label })));
}
function checkPoolKnobs(ctx, out) {
	for (const { knob } of KNOB_BOUNDS) {
		const fn = functionKnob(ctx.pool[knob]);
		if (fn === null) continue;
		const algorithm = asString(fn["algorithm"]);
		if (ctx.autoscalerNames.has(algorithm)) continue;
		out.push(issue("pool-autoscaler-unknown", `${ctx.pointer}/${knob}/algorithm`, APP_POOL_RULE_MESSAGES["pool-autoscaler-unknown"]({
			pool: ctx.label,
			knob,
			algorithm: safeRuleLabel(algorithm)
		})));
	}
	for (const { knob, min, max } of KNOB_BOUNDS) {
		const fn = functionKnob(ctx.pool[knob]);
		if (fn === null) continue;
		const value = knobNumber(fn["max"]);
		if (value === null || value >= min && value <= max) continue;
		out.push(issue("pool-knob-max-out-of-range", `${ctx.pointer}/${knob}/max`, APP_POOL_RULE_MESSAGES["pool-knob-max-out-of-range"]({
			pool: ctx.label,
			knob,
			value,
			min,
			max
		})));
	}
}
/**
* The first segments of the platform's own subject space, imported from their declarations. A
* binding topic whose first segment is one of these would put an app durable on the platform's own
* run envelopes (chain F hop 2) or, for `pool-run`, on its run-preempted notices.
*
* ⛔ EXPORTED SO THE OPERATOR SHARES THIS SET RATHER THAN DECLARING A SECOND ONE (SCC-6). The
* publish rule is the primary enforcement and the DP's bridge is defence in depth for a binding
* that predates it, so the two have to refuse the same inputs. The bridge tested ONE of the
* first three constants and re-applied no grammar, while the value it guards is interpolated straight
* into a live NATS subject filter and the desired-state twin bounds it by length alone.
*
* The fourth member, `APP_POOL_RUN_NOTICE_SEGMENT` (`pool-run`), is the subtree of the operator's
* run-preempted notices (2026-10-24-pool-machine-generations, D-41). A binding on `pool-run` or
* `pool-run.<x>` would turn each notice into a pool submission, so the rule
* `binding-event-topic-reserved` refuses it, and the operator's bridge refuses it through this
* same set.
*/
const RESERVED_FIRST_SEGMENTS = /* @__PURE__ */ new Set([
	APP_POOL_SUBJECT_SEGMENT,
	APP_POOL_BINDING_SEGMENT,
	WORK_SUBJECT_SEGMENT,
	APP_POOL_RUN_NOTICE_SEGMENT
]);
/** Is `topic` legal and outside every reserved subtree? `assertValidTopic` throws on a malformed
*  topic and on the `zion.` prefix; both are the same refusal to a binding author. */
function topicReserved(topic) {
	try {
		assertValidTopic(topic);
	} catch {
		return true;
	}
	return RESERVED_FIRST_SEGMENTS.has(topic.split(".")[0]);
}
function checkBinding(binding, index, poolNames, seen, out) {
	const b = asRecord(binding);
	if (b === null) return;
	const pointer = `/bindings/${index}`;
	const pool = asString(b["pool"]);
	if (pool !== null && !poolNames.has(pool)) out.push(issue("binding-pool-unknown", `${pointer}/pool`, APP_POOL_RULE_MESSAGES["binding-pool-unknown"]({
		index,
		pool: safeRuleLabel(pool)
	})));
	const on = asRecord(b["on"]);
	const schedule = on === null ? null : asString(on["schedule"]);
	if (schedule !== null) {
		const parsed = parseCronExpression(schedule);
		if (!parsed.ok) out.push(issue("binding-schedule-invalid", `${pointer}/on/schedule`, APP_POOL_RULE_MESSAGES["binding-schedule-invalid"]({
			index,
			schedule: safeRuleLabel(schedule),
			reason: parsed.reason
		})));
	}
	const topic = on === null ? null : asString(on["event"]);
	if (topic !== null && topicReserved(topic)) out.push(issue("binding-event-topic-reserved", `${pointer}/on/event`, APP_POOL_RULE_MESSAGES["binding-event-topic-reserved"]({
		index,
		topic: safeRuleLabel(topic),
		segment: safeRuleLabel(topic.split(".")[0])
	})));
	const identity = bindingIdentity(b);
	if (identity !== null) {
		const first = seen.get(identity);
		if (first === void 0) seen.set(identity, index);
		else out.push(issue("binding-duplicate", pointer, APP_POOL_RULE_MESSAGES["binding-duplicate"]({
			index,
			first
		})));
	}
	if (b["payload"] !== void 0) {
		const bytes = serializedByteLength(b["payload"]);
		if (bytes > 65536) out.push(issue("binding-payload-too-large", `${pointer}/payload`, APP_POOL_RULE_MESSAGES["binding-payload-too-large"]({
			index,
			bytes,
			max: APP_POOL_BINDING_PAYLOAD_MAX_BYTES
		})));
	}
}
/** The identity two bindings must share to be duplicates: the pool and the whole trigger. Serialized
*  so `{ schedule: '0 3 * * *' }` and `{ event: 'orders.created' }` compare by value. A binding
*  whose `on` is not an object gets no identity and duplicates nothing. */
function bindingIdentity(b) {
	const on = asRecord(b["on"]);
	if (on === null) return null;
	return JSON.stringify([b["pool"] ?? null, on]);
}
/** The kernel rights whose route is the app-mail relay route (2026-10-09-app-mail, D-18). */
const MAIL_KERNEL_RIGHTS = /* @__PURE__ */ new Set(["mail.send", "mail.external"]);
/**
* `kernel-right-mail-requires-core-api`: an app whose `kernelRights` names a mail right must have
* at least one fixture whose `infrastructure` (or the deprecated `resources`) block declares
* `coreApiAccess: true`.
*
* Inputs: the app manifest as a plain object, and the resolved block of every fixture that parsed
* as a plain object. A `kernelRights` that is not an array, and an item that is not a string, give
* no issue: the schema layer refuses them. Only the literal `true` counts as `coreApiAccess`.
*
* Output: at most one issue per bundle, at `/kernelRights/<index>` of the first mail right in the
* list. Its message names that right. A pool block never counts, because a pool cannot hold a
* kernel right (the release statement admits fixture images only).
*/
function checkMailKernelRightCoreApi(app, fixtureInfrastructure, out) {
	const rights = app["kernelRights"];
	if (!Array.isArray(rights)) return;
	const index = rights.findIndex((right) => typeof right === "string" && MAIL_KERNEL_RIGHTS.has(right));
	if (index === -1) return;
	for (const block of fixtureInfrastructure.values()) if (block["coreApiAccess"] === true) return;
	out.push(issue("kernel-right-mail-requires-core-api", `/kernelRights/${index}`, APP_POOL_RULE_MESSAGES["kernel-right-mail-requires-core-api"]({ right: safeRuleLabel(rights[index]) })));
}
/**
* `kernel-right-mail-external-requires-send` (R-011): an app whose `kernelRights` names
* `mail.external` must also name `mail.send`.
*
* Input: the app manifest as a plain object. A `kernelRights` that is not an array gives no issue,
* and an item that is not a string matches neither name: the schema layer refuses them. The match
* is exact and case-sensitive, so `Mail.send` does not count as `mail.send`.
*
* Output: at most one issue per bundle, at `/kernelRights/<index>` of the first `mail.external`.
* The rule reads no fixture, so it fires beside `kernel-right-mail-requires-core-api` when both
* faults hold. A duplicate or an order fault is the business of the rights order check of each
* door, and this rule ignores it.
*/
function checkMailExternalNeedsSend(app, out) {
	const rights = app["kernelRights"];
	if (!Array.isArray(rights)) return;
	const external = rights.indexOf("mail.external");
	if (external === -1 || rights.includes("mail.send")) return;
	out.push(issue("kernel-right-mail-external-requires-send", `/kernelRights/${external}`, APP_POOL_RULE_MESSAGES["kernel-right-mail-external-requires-send"]({})));
}
/**
* Check every pool, binding and autoscaler in the bundle, and the two mail kernel-right rules.
* Pure, no I/O, never throws.
*
* Inputs: `bundle.app` is `app.json` as parsed JSON, and `bundle.fixtures` is every `fixture.json`
* with its id. Both stay `unknown` all the way down, because at the CLI door these rules run when
* schema validation has already failed. An `app` that is not a plain object yields no issues, and
* the door reports the schema error. An entry of the wrong shape is skipped, so a bundle cannot
* produce a rule issue that contradicts its own schema error.
*
* Output: one `FixtureRuleIssue` per violation. Every one carries `severity: 'error'` and a `rule`
* from `APP_POOL_RULE_IDS`. `path` is a JSON Pointer into `app.json`, such as
* `/pools/build/compute/max/cpu`. The one exception is `pool-image-fixture-unreferenced`, whose
* path points into the fixture and is prefixed `fixtures/<id>`. The order is the rules table in
* `design-docs/WS-1.md` §3: per pool in declaration order, then per fixture, then per binding, then
* the two app-level rules `kernel-right-mail-requires-core-api` and
* `kernel-right-mail-external-requires-send`, in that order (2026-10-09-app-mail WS-1, R-011). One
* pool can raise several issues, and a rule that reads a value it cannot type stays quiet.
*
* Invariants: no rule reads a deprecated key. Every app-authored value reaching a message or a path
* passes `safeRuleLabel` first, the M-1 defence. The numerals come from `app-pool.ts` and are
* restated nowhere. Side effects: none. It touches no clock, no filesystem and no network, so both
* doors get the same answer for the same bundle.
*/
function checkAppPools(bundle) {
	const out = [];
	const app = asRecord(bundle.app);
	if (app === null) return out;
	const pools = asRecord(app["pools"]) ?? {};
	const autoscalerNames = new Set(Object.keys(asRecord(app["autoscalers"]) ?? {}));
	const fixtureIds = new Set(bundle.fixtures.map((f) => f.fixtureId));
	const fixtureInfrastructure = /* @__PURE__ */ new Map();
	for (const f of bundle.fixtures) {
		const manifest = asRecord(f.manifest);
		if (manifest === null) continue;
		fixtureInfrastructure.set(f.fixtureId, asRecord(readFixtureInfrastructure(manifest)) ?? {});
	}
	const poolNames = new Set(Object.keys(pools));
	const fixtureReadWriteBuckets = /* @__PURE__ */ new Set();
	for (const f of bundle.fixtures) {
		const manifest = asRecord(f.manifest);
		if (manifest === null || !runsAtHome(manifest)) continue;
		const blob = asRecord(asRecord(readFixtureInfrastructure(manifest))?.["blobStorage"]);
		if (blob === null) continue;
		for (const [name, mode] of boundBlobModes(blob)) if (mode === "readWrite") fixtureReadWriteBuckets.add(name);
	}
	const globalScopeBuckets = /* @__PURE__ */ new Set();
	const declared = asRecord(app["blobStorage"])?.["buckets"];
	for (const raw of Array.isArray(declared) ? declared : []) {
		const entry = asRecord(raw);
		if (entry === null) continue;
		const declaredName = asString(entry["name"]);
		if (declaredName !== null && entry["scope"] === "global") globalScopeBuckets.add(declaredName);
	}
	for (const [name, raw] of Object.entries(pools)) {
		const pool = asRecord(raw);
		if (pool === null) continue;
		const declaredFixture = asString(asRecord(pool["image"])?.["fixture"]);
		const backingBlock = declaredFixture === null ? void 0 : fixtureInfrastructure.get(declaredFixture);
		const ctx = {
			label: safeRuleLabel(name),
			pointer: `/pools/${pointerSegment(name)}`,
			pool,
			fixtureIds,
			autoscalerNames,
			backingFixture: backingBlock === void 0 ? { resolved: false } : {
				resolved: true,
				block: backingBlock
			},
			backingFixtureId: declaredFixture ?? "",
			fixtureReadWriteBuckets,
			globalScopeBuckets
		};
		checkPoolImage(ctx, out);
		checkPoolInfrastructure(ctx, out);
		checkPoolCompute(ctx, out);
		checkPoolRegion(ctx, out);
		checkPoolBlobStorage(ctx, out);
		checkPoolBlobStorageHome(ctx, out);
		checkPoolConcurrencyReachable(ctx, out);
		checkPoolContainerRuntime(ctx, out);
		checkPoolGpuSandbox(ctx, out);
		checkPoolGpuCount(ctx, out);
		checkPoolPlacementForm(ctx, out);
		checkPoolTierEntries(ctx, out);
		checkPoolTierList(ctx, out);
		checkPoolTierNodeTotal(ctx, out);
		checkPoolGpuTierSandbox(ctx, out);
		checkPoolTierConcurrencyReachable(ctx, out);
		checkPoolPreemptPolicy(ctx, out);
		checkPoolStanding(ctx, out);
		checkPoolKnobs(ctx, out);
	}
	const named = /* @__PURE__ */ new Set();
	for (const raw of Object.values(pools)) {
		const image = asRecord(asRecord(raw)?.["image"]);
		const fixture = image === null ? null : asString(image["fixture"]);
		if (fixture !== null) named.add(fixture);
	}
	for (const { fixtureId, manifest } of bundle.fixtures) {
		const m = asRecord(manifest);
		if (m === null || m["poolImage"] !== true || named.has(fixtureId)) continue;
		out.push(issue("pool-image-fixture-unreferenced", `fixtures/${pointerSegment(fixtureId)}/poolImage`, APP_POOL_RULE_MESSAGES["pool-image-fixture-unreferenced"]({ fixture: safeRuleLabel(fixtureId) })));
	}
	const bindings = Array.isArray(app["bindings"]) ? app["bindings"] : [];
	const seenBindings = /* @__PURE__ */ new Map();
	for (const [index, binding] of bindings.entries()) checkBinding(binding, index, poolNames, seenBindings, out);
	checkMailKernelRightCoreApi(app, fixtureInfrastructure, out);
	checkMailExternalNeedsSend(app, out);
	out.push(...checkBlobBucketRules(bundle));
	return out;
}
//#endregion
export { APP_POOL_RULE_IDS, APP_POOL_RULE_MESSAGES, APP_POOL_SHARED_RULE_IDS, RESERVED_FIRST_SEGMENTS, SERVICE_ACCOUNT_CAPABILITY_MEMBERS, checkAppPools, firstExceededCapability, unservablePoolCapabilities };

//# sourceMappingURL=app-pool-rules.js.map